import { useEffect, useMemo, useRef, useState } from "react";
import {
  type ConfigResponse,
  MAX_RECIPIENTS,
  isValidAddress,
  normalizeRecipients,
} from "@site-alerter/shared";
import { ApiError, putConfig } from "../api.js";

interface Props {
  idToken: string;
  config: ConfigResponse;
  onChange: (config: ConfigResponse) => void;
}

export function Recipients({ idToken, config, onChange }: Props): JSX.Element {
  const [draft, setDraft] = useState<string[]>(config.recipients);
  const [baseline, setBaseline] = useState<string[]>(config.recipients);
  const [version, setVersion] = useState(config.version);
  const [entry, setEntry] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string[] | null>(null);
  const [conflict, setConflict] = useState<ConfigResponse | null>(null);

  const dirty = useMemo(
    () => draft.length !== baseline.length || draft.some((a, i) => a !== baseline[i]),
    [draft, baseline],
  );

  /*
   * Pausing also bumps `version`. Without this, the panel would keep the
   * version it loaded with and the next save would 409 against an edit the
   * user made themselves, one panel over. An in-progress draft is never
   * clobbered — only a clean panel adopts the new list.
   */
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    setVersion(config.version);
    if (!dirtyRef.current) {
      setDraft(config.recipients);
      setBaseline(config.recipients);
    }
  }, [config.version, config.recipients]);

  // Same rules as the server, from the same module — §10 forbids a second copy.
  const validation = useMemo(() => normalizeRecipients(draft), [draft]);
  const entryValid = entry.trim() === "" || isValidAddress(entry.trim());
  const alreadyPresent = draft.includes(entry.trim().toLowerCase());

  function addEntry(): void {
    const address = entry.trim().toLowerCase();
    if (address === "" || !isValidAddress(address) || draft.includes(address)) return;
    setDraft([...draft, address]);
    setEntry("");
  }

  function accept(updated: ConfigResponse): void {
    setDraft(updated.recipients);
    setBaseline(updated.recipients);
    setVersion(updated.version);
    setConflict(null);
    setError(null);
    onChange(updated);
  }

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      accept(await putConfig(idToken, draft, version));
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 409 && cause.payload.current != null) {
        // Show what changed instead of retrying — a silent retry is exactly the
        // invisible lost-edit this endpoint's versioning exists to prevent.
        setConflict(cause.payload.current);
        setVersion(cause.payload.current.version);
      } else if (cause instanceof ApiError) {
        setError(cause.payload.details ?? [cause.message]);
      } else {
        setError([String(cause)]);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Recipients</h2>

      <ul className="recipients">
        {draft.map((address) => (
          <li key={address}>
            <span>{address}</span>
            <button
              type="button"
              aria-label={`Remove ${address}`}
              onClick={() => setDraft(draft.filter((a) => a !== address))}
            >
              ×
            </button>
          </li>
        ))}
        {draft.length === 0 && <li className="muted">No recipients — sending would fail.</li>}
      </ul>

      <form
        className="add-row"
        onSubmit={(event) => {
          event.preventDefault();
          addEntry();
        }}
      >
        <input
          type="email"
          value={entry}
          placeholder="name@example.com"
          aria-invalid={!entryValid}
          onChange={(event) => setEntry(event.target.value)}
        />
        <button
          type="submit"
          disabled={
            entry.trim() === "" ||
            !entryValid ||
            alreadyPresent ||
            draft.length >= MAX_RECIPIENTS
          }
        >
          Add
        </button>
      </form>

      {!entryValid && <p className="error">Not a valid email address.</p>}
      {entryValid && alreadyPresent && entry.trim() !== "" && (
        <p className="muted">Already in the list.</p>
      )}
      {draft.length >= MAX_RECIPIENTS && (
        <p className="muted">At most {MAX_RECIPIENTS} recipients.</p>
      )}
      {!validation.ok && dirty && (
        <ul className="error">
          {validation.details.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      )}
      {error != null && (
        <ul className="error">
          {error.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      )}

      {conflict != null && <Conflict current={conflict} draft={draft} onAccept={accept} />}

      <div className="actions">
        <button
          type="button"
          className="primary"
          disabled={!dirty || busy || !validation.ok}
          onClick={() => void save()}
        >
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" disabled={!dirty || busy} onClick={() => setDraft(baseline)}>
          Discard
        </button>
      </div>
    </section>
  );
}

function Conflict({
  current,
  draft,
  onAccept,
}: {
  current: ConfigResponse;
  draft: string[];
  onAccept: (config: ConfigResponse) => void;
}): JSX.Element {
  const added = current.recipients.filter((a) => !draft.includes(a));
  const removed = draft.filter((a) => !current.recipients.includes(a));

  return (
    <div className="conflict">
      <p>
        <strong>Someone else edited this list.</strong> {current.updatedBy} saved version{" "}
        {current.version}. Your changes were not applied.
      </p>
      <ul>
        {added.map((address) => (
          <li key={`add-${address}`} className="diff-add">
            they have {address}, you do not
          </li>
        ))}
        {removed.map((address) => (
          <li key={`remove-${address}`} className="diff-remove">
            you have {address}, they do not
          </li>
        ))}
        {added.length === 0 && removed.length === 0 && (
          <li className="muted">The lists now match; save again to bump the version.</li>
        )}
      </ul>
      <button type="button" onClick={() => onAccept(current)}>
        Discard mine and load theirs
      </button>
    </div>
  );
}
